import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { Product } from '@/api/types';
import { Card, ErrorNote, Spinner } from '@/components/Common';
import { useAuth } from '@/lib/auth';
import { money } from '@/lib/format';

interface SyncResult {
  productsSynced: number;
  productsDisabled: number;
  drift: Array<{ kind: string; productId: number | string; detail: string }>;
}

export function CataloguePage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['catalogue'],
    queryFn: () => api.get<{ products: Product[] }>('/api/admin/catalogue/products'),
  });

  const syncProducts = useMutation({
    mutationFn: () => api.post<SyncResult>('/api/admin/catalogue/sync'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['catalogue'] }),
  });

  const syncStock = useMutation({
    mutationFn: () => api.post<{ productsQueried: number; bundlesSynced: number; failures: number }>(
      '/api/admin/catalogue/stock/sync'),
  });

  const toggle = useMutation({
    mutationFn: ({ productId, enabled }: { productId: number; enabled: boolean }) =>
      api.patch(`/api/admin/catalogue/products/${productId}?enabled=${enabled}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['catalogue'] }),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;

  const products = data?.products ?? [];

  return (
    <div className="space-y-4">
      {syncProducts.data && syncProducts.data.drift.length > 0 && (
        <Card title={`Drift — ${syncProducts.data.drift.length} change(s) since the last sync`}>
          <ul className="divide-y divide-ink-100">
            {syncProducts.data.drift.map((entry, index) => (
              <li key={index} className="flex gap-3 px-4 py-2 text-sm">
                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-900">
                  {entry.kind}
                </span>
                <span className="tabular text-ink-500">{entry.productId || '—'}</span>
                <span className="text-ink-800">{entry.detail}</span>
              </li>
            ))}
          </ul>
          <p className="border-t border-ink-100 px-4 py-2 text-xs text-ink-500">
            A silently changed increment is exactly the kind of thing that turns into a day of
            failed recharges. Review these each morning.
          </p>
        </Card>
      )}

      <Card
        title={`Products — ${products.length}`}
        actions={
          can('ADMIN', 'OPS') && (
            <div className="flex gap-2">
              <button type="button" className="btn-secondary" disabled={syncProducts.isPending}
                      onClick={() => syncProducts.mutate()}>
                {syncProducts.isPending ? 'Syncing…' : 'Sync products'}
              </button>
              <button type="button" className="btn-secondary" disabled={syncStock.isPending}
                      onClick={() => syncStock.mutate()}>
                {syncStock.isPending ? 'Syncing…' : 'Sync bundles'}
              </button>
            </div>
          )
        }
      >
        {syncStock.data && (
          <p className="m-4 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            {syncStock.data.bundlesSynced} bundle(s) across {syncStock.data.productsQueried}{' '}
            product(s){syncStock.data.failures > 0 && `, ${syncStock.data.failures} failed`}.
          </p>
        )}
        {(syncProducts.isError || syncStock.isError) && (
          <ErrorNote error={syncProducts.error ?? syncStock.error} />
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-ink-200">
              <tr>
                <th className="th">Id</th><th className="th">Name</th>
                <th className="th">Category</th><th className="th">Network</th>
                <th className="th">Wallet</th><th className="th">Currency</th>
                <th className="th text-right">Min</th><th className="th text-right">Max</th>
                <th className="th text-right">Increment</th>
                <th className="th">Needs</th><th className="th">Enabled</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {products.map((product) => (
                <tr key={product.productId}
                    className={product.enabled ? 'hover:bg-ink-50' : 'bg-ink-50 text-ink-400'}>
                  <td className="td tabular">{product.productId}</td>
                  <td className="td font-medium">
                    {product.requiresProductCode ? (
                      <Link to={`/catalogue/${product.productId}/bundles`} className="hover:underline">
                        {product.name}
                      </Link>
                    ) : product.name}
                  </td>
                  <td className="td text-ink-600">{product.category ?? '—'}</td>
                  <td className="td text-ink-600">{product.network ?? '—'}</td>
                  <td className="td tabular">{product.walletTypeId}</td>
                  <td className="td">{product.currency ?? '—'}</td>
                  <td className="td tabular text-right">{money(product.minAmount)}</td>
                  <td className="td tabular text-right">{money(product.maxAmount)}</td>
                  <td className="td tabular text-right">{money(product.amountIncrement)}</td>
                  <td className="td text-xs text-ink-500">
                    {[product.requiresProductCode && 'ProductCode',
                      product.requiresNotifyNumber && 'NotifyNumber']
                      .filter(Boolean).join(', ') || '—'}
                  </td>
                  <td className="td">
                    {can('ADMIN', 'OPS') ? (
                      <button
                        type="button"
                        className={`rounded px-2 py-0.5 text-[11px] font-semibold ${
                          product.enabled ? 'bg-emerald-100 text-emerald-800'
                                          : 'bg-ink-200 text-ink-600'
                        }`}
                        onClick={() => toggle.mutate({
                          productId: product.productId, enabled: !product.enabled,
                        })}
                      >
                        {product.enabled ? 'ENABLED' : 'DISABLED'}
                      </button>
                    ) : (
                      <span>{product.enabled ? 'Yes' : 'No'}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="border-t border-ink-100 px-4 py-2 text-xs text-ink-500">
          Disabling is a Platform-side kill switch, independent of anything upstream says. Products
          that disappear from HOT are disabled automatically and never deleted, because orders
          reference them for the life of the ledger.
        </p>
      </Card>
    </div>
  );
}
